/**
 * <bg-constellation> — drifting stars with dynamic links forming
 * constellations. When two stars come within link-distance, a thin
 * curving line is drawn between them. Reads as a living celestial
 * map. Stem assignments:
 *
 *   Lead vocals    → STAR brightness
 *   Backing vocals → LINK density (more links visible at once)
 *   Drums          → LINK draw speed
 *   Bass           → LINK distance threshold (sub = pulls stars closer)
 *   Guitar         → STAR jitter (transient velocity perturbation)
 *   Percussion     → TWINKLE intensity
 *   Strings        → STAR count cap
 *   Synth          → BLOOM halo around stars
 */

import { readAllStems, mix, clamp } from "./stem-levels.ts";
import { getCanvasDpr, getFrameBudgetMs, getParticleScale } from "./perf-budget.ts";

const MAX_STARS = Math.max(8, Math.floor(80 * getParticleScale()));
const LINK_DIST_BASE = 180;
const FRAME_BUDGET_MS = getFrameBudgetMs("atmosphere");

type Star = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  baseSize: number;
  twinklePhase: number;
};

const rand = (lo: number, hi: number): number => lo + Math.random() * (hi - lo);

class BgConstellation extends HTMLElement {
  #canvas: HTMLCanvasElement | null = null;
  #ctx: CanvasRenderingContext2D | null = null;
  #rafId: number | null = null;
  #lastFrame: number = 0;
  #stars: Star[] = [];
  #w: number = 0;
  #h: number = 0;
  #targetCount: number = 40;
  #resizeObserver: ResizeObserver | null = null;
  #visibilityHandler: (() => void) | null = null;
  #linkPhase: number = 0;

  connectedCallback(): void {
    const canvas = document.createElement("canvas");
    canvas.style.cssText =
      "position:absolute;inset:0;width:100%;height:100%;pointer-events:none;display:block;";
    this.style.position = this.style.position || "fixed";
    this.style.inset = "0";
    this.style.pointerEvents = "none";
    this.style.zIndex = "0";
    this.style.mixBlendMode = "screen";
    this.appendChild(canvas);
    this.#canvas = canvas;
    this.#ctx = canvas.getContext("2d");
    if (this.#ctx === null) return;
    this.#resize();
    this.#seed(40);
    this.#resizeObserver = new ResizeObserver(() => this.#resize());
    this.#resizeObserver.observe(this);
    this.#visibilityHandler = (): void => {
      if (document.hidden) this.#stop();
      else this.#start();
    };
    document.addEventListener("visibilitychange", this.#visibilityHandler);
    this.#start();
  }

  disconnectedCallback(): void {
    this.#stop();
    this.#resizeObserver?.disconnect();
    if (this.#visibilityHandler !== null) {
      document.removeEventListener("visibilitychange", this.#visibilityHandler);
      this.#visibilityHandler = null;
    }
    this.#stars = [];
  }

  #resize(): void {
    if (this.#canvas === null || this.#ctx === null) return;
    const dpr = getCanvasDpr();
    this.#w = this.clientWidth || window.innerWidth;
    this.#h = this.clientHeight || window.innerHeight;
    this.#canvas.width = Math.max(1, Math.floor(this.#w * dpr));
    this.#canvas.height = Math.max(1, Math.floor(this.#h * dpr));
    this.#ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  #seed(n: number): void {
    const want = clamp(n, 3, MAX_STARS);
    while (this.#stars.length < want) {
      this.#stars.push({
        x: rand(0, this.#w),
        y: rand(0, this.#h),
        vx: rand(-6, 6),
        vy: rand(-4, 4),
        baseSize: rand(0.6, 1.6),
        twinklePhase: Math.random() * Math.PI * 2,
      });
    }
    while (this.#stars.length > want) this.#stars.pop();
  }

  #start(): void {
    if (this.#rafId !== null) return;
    this.#lastFrame = performance.now();
    const tick = (now: number): void => {
      const dt = now - this.#lastFrame;
      if (dt >= FRAME_BUDGET_MS) {
        this.#lastFrame = now;
        this.#step(Math.min(50, dt), now);
        this.#render(now);
      }
      this.#rafId = requestAnimationFrame(tick);
    };
    this.#rafId = requestAnimationFrame(tick);
  }

  #stop(): void {
    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
  }

  #step(dt: number, _now: number): void {
    const s = readAllStems();
    // STRINGS → star count cap. Floor halved again (was 8, now 4).
    this.#targetCount = Math.floor(mix(4, MAX_STARS, s.strings));
    this.#seed(this.#targetCount);

    // DRUMS → link-draw phase advance
    this.#linkPhase = (this.#linkPhase + dt * 0.002 * (0.4 + s.drums * 2.0)) % 1;

    // GUITAR → star jitter (transient velocity nudges on each frame)
    const jitter = mix(0, 18, s.guitar);
    const dts = dt / 1000;
    for (const star of this.#stars) {
      // Apply guitar-driven random velocity nudge (clamped)
      if (jitter > 0.01) {
        star.vx += (Math.random() - 0.5) * jitter * dts;
        star.vy += (Math.random() - 0.5) * jitter * dts;
        // Cap velocity so jitter doesn't snowball
        star.vx = clamp(star.vx, -28, 28);
        star.vy = clamp(star.vy, -28, 28);
      }
      star.x += star.vx * dts;
      star.y += star.vy * dts;
      if (star.x < -20) star.x = this.#w + 20;
      else if (star.x > this.#w + 20) star.x = -20;
      if (star.y < -20) star.y = this.#h + 20;
      else if (star.y > this.#h + 20) star.y = -20;
    }
  }

  #render(now: number): void {
    if (this.#ctx === null) return;
    const ctx = this.#ctx;
    const s = readAllStems();
    ctx.clearRect(0, 0, this.#w, this.#h);
    ctx.globalCompositeOperation = "lighter";

    // Illustrious-at-peak: floor 0.40, ceiling 1.0 for star brightness
    const starAlpha = mix(0.40, 1.00, s["lead-vocals"]);
    const starSizeMul = 1.0;                                    // fixed (bass moved to link distance)
    const linkHue = 48;                                         // fixed warm-gold (backing moved to density)
    const linkCurve = 0.10;                                     // mild fixed (guitar moved to jitter)
    const synthHalo = mix(4, 18, s.synth);
    const percTwink = s.percussion;
    // BACKING VOCALS → link density (cap on links rendered)
    const linkOpacityScale = mix(0.35, 1.0, s["backing-vocals"]);
    // BASS → link distance threshold (sub draws stars closer together)
    const linkDist = mix(110, 240, s.bass);
    const linkDistSq = linkDist * linkDist;

    // Render stars first
    for (const star of this.#stars) {
      const twink = (Math.sin(star.twinklePhase + now * 0.001) + 1) / 2;
      const tAdj = mix(0.6, 1.0, twink) + percTwink * 0.4 * twink;
      const sz = star.baseSize * starSizeMul * tAdj;
      const a = starAlpha * tAdj;
      ctx.shadowColor = `hsla(50, 90%, 80%, ${a * 0.85})`;
      ctx.shadowBlur = synthHalo;
      ctx.fillStyle = `hsla(50, 70%, 90%, ${a})`;
      ctx.beginPath();
      ctx.arc(star.x, star.y, sz, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.shadowBlur = 0;

    // Render links — each link reveals along its length based on linkPhase
    ctx.lineWidth = 0.7;
    for (let i = 0; i < this.#stars.length; i += 1) {
      const a = this.#stars[i];
      if (!a) continue;
      for (let j = i + 1; j < this.#stars.length; j += 1) {
        const b = this.#stars[j];
        if (!b) continue;
        const dx = a.x - b.x;
        const dy = a.y - b.y;
        const d2 = dx * dx + dy * dy;
        if (d2 > linkDistSq) continue;
        const t = 1 - d2 / linkDistSq;  // 1 = touching, 0 = link edge
        const alpha = t * 0.35 * linkOpacityScale;
        ctx.strokeStyle = `hsla(${linkHue}, 65%, 70%, ${alpha})`;
        // Curve mid-point offset orthogonal to the link
        const mx = (a.x + b.x) / 2;
        const my = (a.y + b.y) / 2;
        const ox = -dy * linkCurve;
        const oy = dx * linkCurve;
        // Animate the "draw" by clipping with the linkPhase
        const drawT = (this.#linkPhase + (i + j) * 0.03) % 1;
        const segStart = Math.max(0, drawT - 0.4);
        const segEnd = Math.min(1, drawT + 0.05);
        ctx.beginPath();
        // Sample along the quadratic
        const samples = 12;
        for (let k = 0; k <= samples; k += 1) {
          const tt = segStart + (segEnd - segStart) * (k / samples);
          const omt = 1 - tt;
          const x = omt * omt * a.x + 2 * omt * tt * (mx + ox) + tt * tt * b.x;
          const y = omt * omt * a.y + 2 * omt * tt * (my + oy) + tt * tt * b.y;
          if (k === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();
      }
    }

    ctx.globalCompositeOperation = "source-over";
  }
}

export const registerBgConstellation = (): void => {
  if (customElements.get("bg-constellation") !== undefined) return;
  customElements.define("bg-constellation", BgConstellation);
};
